import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  Chip
} from '@mui/material';
import { trackConversion } from '../components/common/GoogleAdsTag';
import { useAuth } from '../contexts/AuthContext';
import FortuneServerPayButton from '../components/fortune/FortuneServerPayButton';
import FortunePayPalWrapper from '../components/fortune/FortunePayPalWrapper';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const FortuneBookingCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData } = useAuth();
  
  // 从location.state获取预约数据
  const bookingData = location.state?.bookingData;
  const fortuneTeller = location.state?.fortuneTeller;
  const bookingId = location.state?.bookingId || new URLSearchParams(location.search).get('bookingId');
  
  // 检查URL参数中是否有支付结果状态
  const urlParams = new URLSearchParams(location.search);
  const paymentSuccess = urlParams.get('success') === 'true' || false;
  const paymentCancelled = urlParams.get('cancelled') === 'true' || false;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(paymentSuccess);
  const [paymentError, setPaymentError] = useState('');
  const [bookingDetails, setBookingDetails] = useState(null);
  const [localFortuneTeller, setFortuneTeller] = useState(fortuneTeller || null);
  
  // 客户信息表单状态
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // 加载预约详情和客户信息
  useEffect(() => {
    const loadBookingDetails = async () => {
      if (bookingId) {
        try {
          setLoading(true);
          const bookingRef = doc(db, 'bookings', bookingId);
          const bookingDoc = await getDoc(bookingRef);
          
          if (bookingDoc.exists()) {
            const data = bookingDoc.data();
            setBookingDetails(data);
            
            // 同时加载命理师信息（如果尚未加载）
            if (!fortuneTeller && data.fortuneTellerId) {
              const ftRef = doc(db, 'fortuneTellers', data.fortuneTellerId);
              const ftDoc = await getDoc(ftRef);
              if (ftDoc.exists()) {
                const ftData = ftDoc.data();
                setFortuneTeller({
                  ...ftData,
                  id: ftDoc.id
                });
              }
            }
            
            // 使用预约记录中的信息初始化客户表单
            setCustomerInfo({
              name: data.customerName || data.userName || userData?.displayName || currentUser?.displayName || '',
              email: data.customerEmail || userData?.email || currentUser?.email || '',
              phone: data.customerPhone || userData?.phone || ''
            });
            
            // 如果URL参数指示支付成功，且预约详情已加载，则跟踪转化
            if (paymentSuccess) {
              trackConversion('AW-16953009461', 'qJVfCLaksq8aELXK6ZM_', {
                'transaction_id': bookingId,
                'value': data.price || 0,
                'currency': 'SGD'
              });
              console.log('通过URL参数跟踪预约转化:', bookingId, data.price);
            }
          } else {
            setError('找不到预约记录');
          }
        } catch (err) {
          console.error('加载预约详情失败:', err);
          setError('加载预约详情失败: ' + err.message);
        } finally {
          setLoading(false);
        }
      } else if (userData || currentUser) {
        // 如果没有bookingId但有用户数据，则使用用户数据初始化表单
        setCustomerInfo({
          name: userData?.displayName || currentUser?.displayName || '',
          email: userData?.email || currentUser?.email || '',
          phone: userData?.phone || ''
        });
      }
    };
    
    loadBookingDetails();
  }, [bookingId, userData, currentUser, paymentSuccess]);
  
  // 如果没有预约数据，重定向回命理师详情页
  useEffect(() => {
    if (!bookingData && !bookingId) {
      navigate('/fortune-teller');
    }
  }, [bookingData, bookingId, navigate]);
  
  // 处理表单变化
  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 保存客户信息到预约记录
  const handleUpdateBookingInfo = async () => {
    if (!bookingId) {
      setError('预约ID不存在，无法更新预约信息');
      return;
    }
    
    // 验证客户信息
    if (!customerInfo.name) {
      setError('请填写您的姓名');
      return;
    }
    
    if (!customerInfo.email && !customerInfo.phone) {
      setError('请至少提供电子邮箱或电话号码中的一项');
      return;
    }
    
    try {
      setLoading(true);
      const bookingRef = doc(db, 'bookings', bookingId);
      
      await updateDoc(bookingRef, {
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        updatedAt: serverTimestamp()
      });
      
      setError('');
    } catch (err) {
      console.error('更新预约客户信息失败:', err);
      setError('更新预约信息失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // 处理支付成功
  const handlePaymentSuccess = async (paymentDetails) => {
    console.log('支付成功:', paymentDetails);
    setIsPaymentSuccess(true);
    setPaymentError('');
    
    // 检查是否是"可能已处理"的支付或需要验证的支付
    if (paymentDetails.possiblyProcessed || paymentDetails.requiresVerification) {
      console.log('支付可能已完成，但需要验证。系统正在验证支付状态...');
      
      try {
        // 等待一段时间，给系统一个机会来处理支付
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // 从数据库获取最新的预约状态
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingDoc = await getDoc(bookingRef);
        
        if (bookingDoc.exists()) {
          const bookingData = bookingDoc.data();
          
          // 如果支付状态已经是completed，则完全正常
          if (bookingData.paymentStatus === 'completed' || bookingData.status === 'confirmed') {
            console.log('验证完成：支付已确认成功');
          } else {
            console.log('支付状态尚未更新，但假设已成功。系统将在后台继续验证...');
            
            try {
              // 更新预约状态为等待验证
              await updateDoc(bookingRef, {
                paymentVerificationNeeded: true,
                paymentPossiblyCompleted: true,
                updatedAt: serverTimestamp()
              });
            } catch (updateError) {
              // 如果权限不足无法更新数据库，将信息存储到localStorage
              console.warn('无法更新预约状态，将验证信息存储到本地:', updateError);
              
              try {
                localStorage.setItem(`booking_verification_${bookingId}`, JSON.stringify({
                  orderId: paymentDetails.orderId,
                  timestamp: Date.now(),
                  needs_verification: true
                }));
                
                // 提示管理员处理
                console.info('已将支付验证信息保存到本地存储。请联系管理员处理付款验证。');
              } catch (e) {
                console.error('无法保存验证信息到本地存储:', e);
              }
            }
          }
        }
      } catch (error) {
        console.error('验证支付状态失败:', error);
        
        // 即使验证失败，我们也保持支付成功状态，确保用户体验顺畅
        console.log('尽管验证失败，但仍视为支付成功以确保良好用户体验');
      }
    }
    
    // 跟踪Google Ads转化 - 使用新的预约转化标签
    if (bookingId) {
      trackConversion('AW-16953009461', 'qJVfCLaksq8aELXK6ZM_', {
        'transaction_id': bookingId,
        'value': bookingData?.price || 0,
        'currency': 'SGD'
      });
      console.log('成功跟踪预约转化:', bookingId, bookingData?.price);
    }
  };
  
  // 处理支付失败
  const handlePaymentError = (error) => {
    console.error('支付失败:', error);
    setPaymentError(`支付处理失败: ${error.message || '未知错误'}`);
  };
  
  // 格式化时间段显示
  const formatTimeSlots = (timeSlots) => {
    if (!timeSlots) return '';
    if (Array.isArray(timeSlots)) {
      return timeSlots.join(', ');
    }
    return timeSlots;
  };
  
  // 计算预约时长
  const calculateDuration = (timeSlots) => {
    if (!timeSlots) return 30;
    if (Array.isArray(timeSlots)) {
      return timeSlots.length * 30;
    }
    return 30;
  };
  
  // 如果没有预约数据，显示加载状态
  if (!bookingData && !bookingId) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  // 支付成功后显示的内容
  if (isPaymentSuccess) {
    // 使用bookingDetails代替bookingData（如果存在）
    const displayData = bookingDetails || bookingData;
    
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            预约支付成功！
          </Typography>
          <Typography variant="body1" paragraph>
            您已成功预约并支付 {localFortuneTeller?.name || fortuneTeller?.name || displayData?.fortuneTellerName || '命理师'} 的占卜咨询服务
          </Typography>
          
          <Box sx={{ maxWidth: 500, mx: 'auto', mb: 4, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>
              预约详情：
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    预约日期：{displayData?.formattedDate ? new Date(displayData.formattedDate).toLocaleDateString() : '未知日期'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    预约时间：{formatTimeSlots(displayData?.timeSlots)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    咨询费用：S${displayData?.price?.toFixed(2) || '0.00'}（已支付）
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1">
                    预约人：{customerInfo.name || displayData?.customerName || displayData?.userName || '未知用户'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto', mb: 4, textAlign: 'left' }}>
            <Typography variant="body2">
              命理师将通过您提供的联系方式（{customerInfo.phone ? '电话' : ''}
              {customerInfo.phone && customerInfo.email ? '和' : ''}
              {customerInfo.email ? '邮箱' : ''}）与您联系。
              您也可以在个人中心的"预约历史"中查看此预约的详细信息。
            </Typography>
          </Alert>
          
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/user/bookings')}
              sx={{ mr: 2 }}
            >
              查看我的预约
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/fortune-teller')}
            >
              返回命理师列表
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        返回命理师详情
      </Button>
      
      <Typography variant="h4" gutterBottom>
        确认预约并付款
      </Typography>
      
      <Grid container spacing={3}>
        {/* 左侧：预约详情 */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              预约详情
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={fortuneTeller?.photoURL || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'}
                alt={fortuneTeller?.name || '命理师'}
                sx={{ width: 60, height: 60, mr: 2 }}
              />
              <Box>
                <Typography variant="h6">
                  {fortuneTeller?.name || '命理师'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fortuneTeller?.title || '专业命理师'} | {fortuneTeller?.experience || '多年'}年经验
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                  {fortuneTeller?.specialties?.slice(0, 2).map((specialty, index) => (
                    <Chip
                      key={index}
                      label={specialty}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  预约日期：
                </Typography>
                <Typography variant="body1">
                  {new Date(bookingData?.formattedDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  预约时间：
                </Typography>
                <Typography variant="body1">
                  {formatTimeSlots(bookingData?.timeSlots)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  预约时长：
                </Typography>
                <Typography variant="body1">
                  {calculateDuration(bookingData?.timeSlots)}分钟
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  预约费用：
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary.main">
                  S${bookingData?.price?.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
            
            {bookingData?.notes && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  备注信息：
                </Typography>
                <Typography variant="body2">
                  {bookingData.notes}
                </Typography>
              </>
            )}
          </Paper>
          
          {/* 客户信息表单 */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              联系信息
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              请提供您的联系信息，以便命理师与您取得联系。姓名为必填项，电话或邮箱至少填写一项。
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="姓名"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleInfoChange}
                  required
                  error={!customerInfo.name}
                  helperText={!customerInfo.name && "姓名为必填项"}
                  InputProps={{
                    startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="电子邮箱"
                  name="email"
                  type="email"
                  value={customerInfo.email}
                  onChange={handleInfoChange}
                  error={!customerInfo.email && !customerInfo.phone}
                  helperText={!customerInfo.email && !customerInfo.phone && "邮箱或电话至少填写一项"}
                  InputProps={{
                    startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="电话号码"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleInfoChange}
                  error={!customerInfo.email && !customerInfo.phone}
                  helperText={!customerInfo.email && !customerInfo.phone && "邮箱或电话至少填写一项"}
                  InputProps={{
                    startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateBookingInfo}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : '保存联系信息'}
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* 右侧：支付部分 */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              支付预约费用
            </Typography>
            
            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {paymentError}
              </Alert>
            )}
            
            <Box sx={{ my: 3, p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                费用摘要
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  咨询费用 ({calculateDuration(bookingData?.timeSlots)}分钟)
                </Typography>
                <Typography variant="body2">
                  S${bookingData?.price?.toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  总计
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                  S${bookingData?.price?.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              请确保您已填写联系信息，并仔细核对预约详情。支付成功后，预约将被确认。
            </Alert>
            
            {/* PayPal支付按钮 - 前端集成方式 */}
            <Box sx={{ mt: 2 }}>
              <FortuneServerPayButton
                bookingId={bookingId}
                amount={bookingData?.price}
                onSuccess={handlePaymentSuccess}
                onErrorCallback={handlePaymentError}
              />
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              点击上方按钮将打开PayPal支付页面，完成支付后将自动返回
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FortuneBookingCheckout;